import { useEffect, useRef } from "react";

interface UseNavigationBlockerProps {
  shouldBlock: boolean;
  message?: string;
}

export const useNavigationBlocker = ({
  shouldBlock,
  message = "You have unsaved changes. Are you sure you want to leave?",
}: UseNavigationBlockerProps) => {
  const navigationBlocked = useRef(false);
  const originalHistoryMethods = useRef<{
    pushState: typeof window.history.pushState;
    replaceState: typeof window.history.replaceState;
    back: typeof window.history.back;
    forward: typeof window.history.forward;
    go: typeof window.history.go;
  } | null>(null);

  // Store original history methods
  useEffect(() => {
    if (!originalHistoryMethods.current) {
      originalHistoryMethods.current = {
        pushState: window.history.pushState,
        replaceState: window.history.replaceState,
        back: window.history.back,
        forward: window.history.forward,
        go: window.history.go,
      };
    }
  }, []);

  // Main navigation blocking effect
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlock) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        // Push the current state back to prevent navigation
        window.history.pushState(null, "", window.location.href);
        console.log("Back/Forward navigation blocked");
      }
    };

    // Set navigation blocked flag
    navigationBlocked.current = shouldBlock;

    if (shouldBlock) {
      // Add event listeners
      window.addEventListener("beforeunload", handleBeforeUnload);
      window.addEventListener("popstate", handlePopState);

      // Create history buffer to prevent back navigation
      window.history.pushState({ blocked: true }, "", window.location.href);
      window.history.pushState({ blocked: true }, "", window.location.href);
      window.history.pushState({ blocked: true }, "", window.location.href);

      // Override history methods
      if (originalHistoryMethods.current) {
        window.history.pushState = function (...args) {
          if (navigationBlocked.current) {
            console.log("History.pushState blocked");
            return;
          }
          return originalHistoryMethods.current!.pushState.apply(this, args);
        };

        window.history.replaceState = function (...args) {
          if (navigationBlocked.current) {
            console.log("History.replaceState blocked");
            return;
          }
          return originalHistoryMethods.current!.replaceState.apply(this, args);
        };

        window.history.back = function () {
          if (navigationBlocked.current) {
            console.log("History.back blocked");
            return;
          }
          return originalHistoryMethods.current!.back.apply(this);
        };

        window.history.forward = function () {
          if (navigationBlocked.current) {
            console.log("History.forward blocked");
            return;
          }
          return originalHistoryMethods.current!.forward.apply(this);
        };

        window.history.go = function (delta: number) {
          if (navigationBlocked.current) {
            console.log("History.go blocked");
            return;
          }
          return originalHistoryMethods.current!.go.apply(this, [delta]);
        };
      }
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);

      // Restore original history methods
      if (originalHistoryMethods.current) {
        window.history.pushState = originalHistoryMethods.current.pushState;
        window.history.replaceState =
          originalHistoryMethods.current.replaceState;
        window.history.back = originalHistoryMethods.current.back;
        window.history.forward = originalHistoryMethods.current.forward;
        window.history.go = originalHistoryMethods.current.go;
      }
    };
  }, [shouldBlock, message]);

  // Function to clear history buffer when unblocking
  const clearHistoryBuffer = () => {
    if (!shouldBlock && navigationBlocked.current) {
      try {
        window.history.go(-3); // Go back 3 states to clear the buffer
      } catch (e) {
        console.log("History cleanup failed:", e);
      }
    }
    navigationBlocked.current = false;
  };

  // Safe navigate function that respects blocking
  const safeNavigate = (navigationFn: () => void) => {
    if (navigationBlocked.current) {
      console.log("Navigation blocked");
      return false;
    }
    navigationFn();
    return true;
  };

  return {
    isBlocked: navigationBlocked.current,
    clearHistoryBuffer,
    safeNavigate,
  };
};
